import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Alert, Collapse } from 'reactstrap';
import validator from 'validator';
import { clearFields, Field } from 'redux-form';
import {
  changeLoadingFalse,
  changeLoadingTrue,
} from '../../../redux/actions/loadingActions';
import MatTable from '../../../shared/components/material-table';
import shareLinkColumns from './SharedLinksColumns';
import {
  schoolSharedLinkFindAll,
  schoolSharedLinkIdMutation,
  schoolSharedLinkSaveMutation,
} from '../../../shared/queries/SchoolSharedLink';
import GraphQL from '../../../GraphQL';
import Modal from '../../../shared/components/Modal';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import renderDropZoneField from '../../../shared/components/form/DropZone';
import DynamicNavigationTabs from '../../../shared/components/DynamicNavigationTabs';
import { ACTIVE_TAB } from '../../../shared/utils/constants';
import { onShowAlert, upLoadFile } from '../../../shared/utils/smartutils';

const AboutSchoolSection = (props) => {
  const { t, dispatch } = props;
  const [activeTab, setActiveTab] = useState(ACTIVE_TAB.ONE);

  function showLoading() {
    dispatch(changeLoadingTrue());
  }

  function hideLoading() {
    dispatch(changeLoadingFalse());
  }

  const [schoolSharedLinks, setSchoolSharedLinks] = useState([]);
  const [selectedLinkToRemove, setSelectedLinkToRemove] = useState([]);
  const [removeSharedLinkModal, setRemoveSharedLinkModal] = useState(false);
  const [linkToShare, setLinkToShare] = useState({
    description: '',
    link: '',
    file: null,
  });

  const [notificationAlert, setNotificationAlert] = useState({});

  const loadSharedLinks = () => {
    showLoading();
    const query = schoolSharedLinkFindAll();
    GraphQL(query, {})
      .then((res) => {
        setSchoolSharedLinks(res.schoolSharedLinkFindAll);
        hideLoading();
      })
      .catch((error) => {
        hideLoading();
        console.error(error);
      });
  };

  const toggleRemoveSharedLinkModal = () => {
    setRemoveSharedLinkModal(!removeSharedLinkModal);
  };

  const removeSharedLink = () => {
    showLoading();
    toggleRemoveSharedLinkModal();
    const mutation = schoolSharedLinkIdMutation(selectedLinkToRemove.id);
    GraphQL(mutation, {})
      .then(() => {
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-removed')
        );
        loadSharedLinks();
      })
      .catch((error) => {
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-remove')
        );
        console.error(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const removeSharedLinkClickHandler = (evt, rowData) => {
    setSelectedLinkToRemove({ ...rowData });
    toggleRemoveSharedLinkModal();
  };

  const saveLink = (values) => {
    showLoading();
    const saveMutation = schoolSharedLinkSaveMutation(values);
    GraphQL(saveMutation, {})
      .then(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
        setLinkToShare({ description: '', link: '', file: null });
        loadSharedLinks();
      })
      .catch((error) => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-save')
        );
        console.error(error);
      });
  };

  const onFileChange = (files) => {
    const updatedLinkToShare = { ...linkToShare };
    updatedLinkToShare.file = files && files.length ? files[0] : null;
    setLinkToShare(updatedLinkToShare);
  };

  const clearFile = () => {
    dispatch(clearFields('DataSchoolForm', true, true, ...['fileToShare']));
  };

  const handleSaveLink = async () => {
    const updatedLinkToShare = { ...linkToShare };
    if (activeTab === ACTIVE_TAB.ONE) {
      const uploadedFile = await upLoadFile(updatedLinkToShare.file);
      updatedLinkToShare.link = uploadedFile.link;
      clearFile();
    }
    saveLink(updatedLinkToShare);
  };

  const hasMessageError = () => {
    let message = '';
    if (!linkToShare.description) {
      message = t('validations.no-description');
    }
    if (activeTab === ACTIVE_TAB.ONE) {
      if (!linkToShare.file) {
        message = t('validations.no-file-attached');
      }
    }
    if (activeTab === ACTIVE_TAB.TWO) {
      if (!linkToShare.link) {
        message = t('validations.share-url');
      }
      if (!validator.isURL(linkToShare.link)) {
        message = t('validations.valid-url');
      }
    }
    if (message) {
      onShowAlert('danger', 'errors.title', message, setNotificationAlert);
    }
    return !!message;
  };

  const addLinkClickHandler = () => {
    if (!hasMessageError()) {
      handleSaveLink();
    }
  };

  const onInputChange = (event) => {
    const updatedLinkToShare = { ...linkToShare };
    updatedLinkToShare[event.currentTarget.name] = event.currentTarget.value;
    setLinkToShare(updatedLinkToShare);
  };

  useEffect(() => {
    loadSharedLinks();
  }, []);

  return (
    <>
      <Modal
        color="warning"
        title={t('notifications.attention')}
        btn="Warning"
        message={t('notifications.delete_modal')}
        toggle={toggleRemoveSharedLinkModal}
        modal={removeSharedLinkModal}
        onConfirm={removeSharedLink}
      />
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t('school_data.link-description')}
        </span>
        <div className="form_form-group-field">
          <div className="form__form-group-input-wrap form__form-group-input-wrap--error-above">
            <input
              placeholder={t('school_data.placeholders.link-description')}
              type="text"
              maxLength={50}
              name="description"
              onChange={onInputChange}
              value={linkToShare.description}
            />
          </div>
        </div>
      </div>
      <DynamicNavigationTabs callBackForActiveTabUpdate={setActiveTab}>
        <div
          title={t('general.file')}
          className="form__form-group d-flex flex-column align-items-end"
        >
          <div className="form__form-group">
            <div className="form__form-group-field">
              <Field
                name="fileToShare"
                component={renderDropZoneField}
                onChange={onFileChange}
              />
            </div>
          </div>
          <Button color="success" type="button" onClick={addLinkClickHandler}>
            {t('general.add')}
          </Button>
        </div>
        <div
          title={t('school_data.link')}
          className="form__form-group d-flex flex-column align-items-end"
        >
          <div className="form__form-group">
            <div className="form_form-group-field">
              <div className="form__form-group-input-wrap form__form-group-input-wrap--error-above">
                <input
                  placeholder={t('school_data.placeholders.link')}
                  type="text"
                  maxLength={300}
                  name="link"
                  onChange={onInputChange}
                  value={linkToShare.link}
                />
              </div>
            </div>
          </div>
          <Button color="success" type="button" onClick={addLinkClickHandler}>
            {t('general.add')}
          </Button>
        </div>
      </DynamicNavigationTabs>
      <Collapse isOpen={notificationAlert.status}>
        <Alert color={notificationAlert.color} className="text-center">
          {t(notificationAlert.message)}
        </Alert>
      </Collapse>
      <div className="form__form-group">
        <MatTable
          data={schoolSharedLinks}
          columns={shareLinkColumns(t)}
          onRemoveClick={removeSharedLinkClickHandler}
          toolbar={false}
          pageSize={10}
        />
      </div>
    </>
  );
};

AboutSchoolSection.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(AboutSchoolSection)
);
